import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { NgApexchartsModule } from 'ng-apexcharts';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule}  from '@angular/material/form-field';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatBadgeModule } from '@angular/material/badge';
import { MatExpansionModule } from '@angular/material/expansion';

import { RequestInterceptor } from './request.interceptor';
import { SpinnerService } from './services/spinner.service';
import { BroadcastService } from './services/broadcast.service';
import { AuthService } from './services/auth.service';
import { ResetPasswordService } from './services/reset-password.service';
import { TransactionService } from './services/transaction.service';
import { ManagerService } from './services/manager.service';
import { WalletService } from './services/wallet.service';
import { SearchFilterBuilderService } from './services/search-filter-builder.service';
import { PaymentMethodService } from './services/payment-method.service';
import { NotificationService } from './services/notification.service';
import { PendingPaymentRequestService } from './services/pending-payment-request.service';
import { RefundTypeService } from './services/refund-type.service';

import { LocalTimePipe } from './pipes/local-time.pipe';

import { AppComponent } from './app.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { LogoComponent } from './logo/logo.component';
import { BaseComponent } from './base/base.component';
import { SecuredBaseComponent } from './base/secured-base.component';
import { ToastComponent } from './toast/toast.component';
import { LoginComponent } from './login/login.component';
import { ResetComponent } from './reset/reset.component';
import { FooterComponent } from './footer/footer.component';
import { MainComponent } from './main/main.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { NavbarComponent } from './navbar/navbar.component';
import { AccountMenuComponent } from './account-menu/account-menu.component';
import { ProfileComponent } from './profile/profile.component';
import { TransactionsComponent } from './transactions/transactions.component';
import { TransactionComponent } from './transaction/transaction.component';
import { PaginationComponent } from './pagination/pagination.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { SectionTitleComponent } from './section-title/section-title.component';
import { FilterPeriodsComponent } from './filter-periods/filter-periods.component';
import { CryptoAssetsTotalComponent } from './crypto-assets-total/crypto-assets-total.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { PendingPaymentRequestsComponent } from './pending-payment-requests/pending-payment-requests.component';
import { PendingPaymentRequestComponent } from './pending-payment-request/pending-payment-request.component';

@NgModule({
  declarations: [
    AppComponent,
    LocalTimePipe,
    SpinnerComponent,
    LogoComponent,
    BaseComponent,
    SecuredBaseComponent,
    ToastComponent,
    LoginComponent,
    ResetComponent,
    FooterComponent,
    MainComponent,
    DashboardComponent,
    ToolbarComponent,
    NavbarComponent,
    AccountMenuComponent,
    ProfileComponent,
    TransactionsComponent,
    TransactionComponent,
    PaginationComponent,
    ConfirmDialogComponent,
    SectionTitleComponent,
    FilterPeriodsComponent,
    CryptoAssetsTotalComponent,
    NotificationsComponent,
    PendingPaymentRequestsComponent,
    PendingPaymentRequestComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    NgApexchartsModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatSnackBarModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    MatToolbarModule,
    MatDividerModule,
    MatMenuModule,
    MatSelectModule,
    MatDatepickerModule,
    MatTableModule,
    MatDialogModule,
    MatCheckboxModule,
    MatListModule,
    ClipboardModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatButtonToggleModule,
    MatRadioModule,
    MatBadgeModule,
    MatExpansionModule
  ],
  providers: [
    RequestInterceptor,
    SpinnerService,
    BroadcastService,
    AuthService,
    ResetPasswordService,
    TransactionService,
    ManagerService,
    WalletService,
    SearchFilterBuilderService,
    PaymentMethodService,
    NotificationService,
    PendingPaymentRequestService,
    RefundTypeService,
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 }},
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: {
        parse: {
          dateInput: 'L',
        },
        display: {
          dateInput: 'L',
          monthYearLabel: 'DD/MM/YYYY',
          dateA11yLabel: 'L',
          monthYearA11yLabel: 'DD/MM/YYYY',
        }
      }
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
